/**
 * @typedef { import('./index.js').Observer } Observer
 */

import { isEquivalent } from '@nrk/reactive-element';

export const THRESHOLD_ATTRIBUTE = 'intersectionthreshold';

/**
 * Created observers are stored in a map, and can be used to observe multiple elements.
 * Unused observers are removed on disconnect. Entries are keyed by config.
 */
const intersectionObservers = new Map();
const hasIntersectionObserver = 'IntersectionObserver' in window && 'IntersectionObserverEntry' in window;

/**
 * Element intersecting mixin.
 * Uses native IntersectionObserver to set "intersecting" reactive property.
 * Configure IntersectionObserver thresholds with "intersectionthreshold" attribute.
 * @template { import('@nrk/reactive-element').ReactiveElementBase } BaseClass
 * @param { BaseClass } Base
 */
export function IntersectingMixin(Base) {
  return class IntersectingElement extends Base {
    static get reactiveProperties() {
      return {
        ...super.reactiveProperties,
        intersectionEntry: Object,
        isIntersecting: Boolean,
      };
    }

    /**
     * Constructor
     * @param  {...any} args
     */
    constructor(...args) {
      super(...args);
      /**
       * The current IntersectionObserverEntry object when intersecting
       * @type { IntersectionObserverEntry | undefined }
       * */
      this.intersectionEntry = undefined;
      /**
       * The intersection threshold as defined by the 'THRESHOLD_ATTRIBUTE' attribute
       * @type { Array<number> }
       */
      this.intersectionThreshold = [0];
      /**
       * Flag indicating whether the element is currently intersecting
       * @type { boolean }
       */
      this.isIntersecting = false;
      /** @type { Observer | undefined } */
      this._intersectionObserver;
    }

    connectedCallback() {
      if (!hasIntersectionObserver) {
        // Intersection observer is not supported and no polyfill was provided.
        super.connectedCallback();
        return;
      }

      const threshold = this.getAttribute(THRESHOLD_ATTRIBUTE);

      if (threshold !== null) {
        this.intersectionThreshold = JSON.parse(threshold);
      }

      this._intersectionObserver = this._getIntersectionObserver(this.intersectionThreshold);

      if (this._intersectionObserver) {
        this._intersectionObserver.observe(this);
        this._intersectionObserver.count++;
      }

      super.connectedCallback?.();
    }

    disconnectedCallback() {
      super.disconnectedCallback && super.disconnectedCallback();

      if (this._intersectionObserver) {
        this._intersectionObserver.unobserve(this);

        if (--this._intersectionObserver.count <= 0) {
          // Destroy unused observer
          this._intersectionObserver.disconnect();
          intersectionObservers.delete(this.intersectionThreshold);
        }

        this._intersectionObserver = undefined;
      }
    }

    /**
     * @param { Array<number> } threshold
     */
    _getIntersectionObserver(threshold) {
      for (const [key, observer] of intersectionObservers.entries()) {
        // Check for existing observer with identical config
        if (isEquivalent(threshold, key)) {
          // In order to delete unused IntersectionObserver instance,
          // we need to store the same key
          this.intersectionThreshold = key;
          return observer;
        }
      }

      const config = { threshold };
      const intersectionObserver = this._createIntersectionObserver(config);
      intersectionObservers.set(threshold, intersectionObserver);

      return intersectionObserver;
    }

    /**
     * @param { IntersectionObserverInit } config
     * @returns { Observer }
     */
    _createIntersectionObserver(config) {
      const intersectionObserver = /** @type { Observer } */ (
        new IntersectionObserver((entries, observer) => {
          for (const entry of entries) {
            const target = /** @type { IntersectingElement } */ (entry.target);
            // @ts-ignore
            target.intersectionEntry = entry;
            target.isIntersecting = entry.isIntersecting;
          }
        }, config)
      );

      // Track how many elements are watched by observer to allow for cleanup
      intersectionObserver.count = 0;

      return intersectionObserver;
    }
  };
}
